import { InfluencerLocation } from '@foodfluence/shared';
import { PlaceDetails } from '../../types';

const STORAGE_KEY = 'bounty_board_location';

export type BoardNearMeLocation = PlaceDetails & { radius: number };

export const loadBoardNearMeLocation = (): BoardNearMeLocation | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BoardNearMeLocation;
    if (typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveBoardNearMeLocation = (location: BoardNearMeLocation | null): void => {
  if (!location) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
};

/** Seed from Explore's saved location filter when the bounties board has none yet. */
export const loadExploreLocationFallback = (): BoardNearMeLocation | null => {
  try {
    const raw = localStorage.getItem('explore_filters');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { location?: BoardNearMeLocation | null; searchRadius?: number };
    if (!parsed.location || typeof parsed.location.lat !== 'number') return null;
    return {
      ...parsed.location,
      radius: parsed.location.radius ?? parsed.searchRadius ?? 25,
    };
  } catch {
    return null;
  }
};

export const influencerLocationToBoardLocation = (location: InfluencerLocation): BoardNearMeLocation => ({
  lat: location.lat,
  lng: location.lng,
  city: location.city,
  state: location.state,
  country: location.country ?? '',
  fullAddress: [location.city, location.state].filter(Boolean).join(', '),
  streetNumber: '',
  streetName: '',
  zip: location.zipCode ?? '',
  radius: location.maxCollabDistance ?? 25,
});

export const boardLocationToInfluencerLocations = (location: BoardNearMeLocation): InfluencerLocation[] => [{
  docId: 'board-near-me',
  influencerId: '',
  city: location.city,
  state: location.state,
  lat: location.lat,
  lng: location.lng,
  maxCollabDistance: location.radius,
  geohash: '',
  isPredicted: false,
  locationPriority: 1,
}];

export const boardLocationLabel = (
  location: BoardNearMeLocation | null,
  fallback?: InfluencerLocation | null,
): string => {
  if (location?.city?.trim()) {
    return location.state?.trim() ? `${location.city}, ${location.state}` : location.city;
  }
  if (fallback?.city?.trim()) {
    return fallback.state?.trim() ? `${fallback.city}, ${fallback.state}` : fallback.city;
  }
  return 'your location';
};
